import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaBalanceScale,
  FaChartLine,
  FaCheckCircle,
  FaFileInvoiceDollar,
  FaMoneyCheckAlt,
  FaPlus,
  FaReceipt,
  FaSearch,
  FaWallet,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { hasAccess, readStoredAdminUser } from './adminAccessControl';
import { buildAccountingReportCenterPath } from './reportCenterLinks';
import './AccountingSetup.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const isSuccessResponse = value => value === 0 || value === '0';
const FINANCE_CATEGORY_OPTIONS = [
  { value: 'rent', label: 'Sewa' },
  { value: 'utilities', label: 'Utilitas' },
  { value: 'maintenance', label: 'Perawatan' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'supplies', label: 'ATK / Supplies' },
  { value: 'salary_support', label: 'Support payroll' },
  { value: 'tax', label: 'Pajak / Retribusi' },
  { value: 'other', label: 'Lain-lain' },
];
const FINANCE_SETTLEMENT_OPTIONS = [
  { value: 'cash', label: 'Bayar tunai' },
  { value: 'bank_transfer', label: 'Transfer / Bank' },
  { value: 'qris', label: 'QRIS' },
  { value: 'other', label: 'Akun asset lain' },
  { value: 'payable', label: 'Catat sebagai hutang' },
];
const FINANCE_STATUS_LABELS = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  partial_paid: 'Partial paid',
  cancelled: 'Cancelled',
};
const FINANCE_APPROVAL_LABELS = {
  not_required: 'Langsung posting',
  pending: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
};
const OWNER_REPORT_FLOW = [
  { title: 'Daily control', copy: 'Cek beban keluar hari ini, hutang baru, dan dokumen yang jatuh tempo.' },
  { title: 'Weekly review', copy: 'Pantau kategori beban paling besar dan payment hutang yang sudah berjalan.' },
  { title: 'Monthly owner pack', copy: 'Laba rugi, neraca, hutang outstanding, dan expense breakdown outlet.' },
  { title: 'Audit trail', copy: 'Pastikan jurnal terbaru, akun lawan, dan nomor dokumen tercatat konsisten.' },
];
const WORKSPACE_OPTIONS = [
  {
    key: 'overview',
    title: 'Ringkasan',
    copy: 'Lihat angka owner, hutang outstanding, dan langkah review.',
  },
  {
    key: 'expense',
    title: 'Catat Beban',
    copy: 'Input beban langsung atau catat vendor bill sebagai hutang.',
  },
  {
    key: 'payable',
    title: 'Bayar Hutang',
    copy: 'Lunasi dokumen hutang yang masih outstanding.',
  },
  {
    key: 'report',
    title: 'Laporan',
    copy: 'Buka aktivitas jurnal, riwayat dokumen, dan histori payment.',
  },
];

const getTodayInputValue = () => new Date().toISOString().split('T')[0];

const createEmptyFinanceForm = () => ({
  settlement_mode: 'cash',
  expense_date: getTodayInputValue(),
  due_date: getTodayInputValue(),
  vendor_name: '',
  category: 'utilities',
  description: '',
  reference_no: '',
  amount: '',
  expense_account_id: '',
  payment_account_id: '',
  payable_account_id: '',
  payment_method: 'cash',
  notes: '',
});

const createEmptyPayablePaymentForm = () => ({
  document_id: '',
  payment_date: getTodayInputValue(),
  amount: '',
  payment_method: 'bank_transfer',
  payment_account_id: '',
  notes: '',
});

const parseJsonResponse = async response => {
  const raw = await response.text();
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return {
      error: 1,
      message: raw,
    };
  }
};

const buildFinanceApiErrorMessage = (response, data, fallback) => {
  if (response?.status === 404) {
    return 'Endpoint Expenses & Payables belum aktif di backend pusat. Restart backend lalu buka ulang halaman ini.';
  }
  return data?.message || fallback;
};

const formatCurrency = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    maximumFractionDigits: 0,
  })}`;

const isWholeRupiahValue = value => {
  const numericValue = Number(value || 0);
  return Number.isFinite(numericValue) && Math.abs(numericValue - Math.round(numericValue)) < 0.0001;
};

const normalizeApprovalStatus = value => String(value || '').trim().toLowerCase() || 'not_required';

const isFinanceDocumentApprovedForLedger = value =>
  ['not_required', 'approved'].includes(normalizeApprovalStatus(value));

const formatShortDate = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateTime = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMonthYear = (year, month) => {
  if (!year || !month) {
    return '-';
  }

  return new Date(year, month - 1, 1).toLocaleDateString('id-ID', {
    month: 'long',
    year: 'numeric',
  });
};

const humanizeToken = value =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ') || '-';

const formatOutletRecord = outlet => ({
  id: String(outlet?.id ?? outlet?.ID ?? ''),
  name: outlet?.outlet_name || outlet?.OutletName || outlet?.name || `Outlet #${outlet?.id || outlet?.ID || '-'}`,
});

const formatCentralAccountRecord = account => ({
  id: String(account?.id ?? account?.ID ?? ''),
  account_id: String(account?.account_id ?? account?.AccountID ?? '').trim(),
  account_name: account?.account_name ?? account?.AccountName ?? '',
  account_type: account?.account_type ?? account?.AccountType ?? '',
  balance:
    account?.balance?.Float64 ??
    account?.Balance?.Float64 ??
    account?.balance ??
    account?.Balance ??
    0,
});

const getFinanceStatusVariant = value => {
  switch (String(value || '').toLowerCase()) {
    case 'paid':
      return '';
    case 'partial_paid':
      return 'is-warning';
    case 'cancelled':
      return 'is-danger';
    default:
      return 'is-danger';
  }
};

const getFinanceApprovalVariant = value => {
  switch (normalizeApprovalStatus(value)) {
    case 'pending':
      return 'is-warning';
    case 'rejected':
      return 'is-danger';
    default:
      return '';
  }
};

function FitnessFinanceManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = readStoredAdminUser();
  const canAccess = hasAccess('/operasional-finance', currentUser);
  const canOpenAnalyticsCenter = hasAccess('/report-center', currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const currentPeriod = useMemo(() => {
    const today = new Date();
    return {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      label: formatMonthYear(today.getFullYear(), today.getMonth() + 1),
    };
  }, []);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId);
  const [centralAccounts, setCentralAccounts] = useState([]);
  const [financeForm, setFinanceForm] = useState(createEmptyFinanceForm);
  const [payablePaymentForm, setPayablePaymentForm] = useState(createEmptyPayablePaymentForm);
  const [financeDocuments, setFinanceDocuments] = useState([]);
  const [pendingApprovalDocuments, setPendingApprovalDocuments] = useState([]);
  const [financePayments, setFinancePayments] = useState([]);
  const [ownerSummary, setOwnerSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingFinanceDocument, setSavingFinanceDocument] = useState(false);
  const [savingFinancePayment, setSavingFinancePayment] = useState(false);
  const [processingApproval, setProcessingApproval] = useState(false);
  const [activeWorkspace, setActiveWorkspace] = useState('overview');
  const [financeApiError, setFinanceApiError] = useState('');
  const [canApproveBackdatedFinance, setCanApproveBackdatedFinance] = useState(false);

  const resolveToken = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return '';
    }

    const isValid = await verifyAndRefreshToken(token);
    if (!isValid) {
      navigate('/loginadmin');
      return '';
    }

    const refreshedToken = localStorage.getItem('sessionToken');
    if (!refreshedToken) {
      navigate('/loginadmin');
      return '';
    }

    return refreshedToken;
  }, [navigate, verifyAndRefreshToken]);

  const fetchOutlets = useCallback(async token => {
    const response = await fetch(`${API_BASE_URL}outletlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ limit: 200, start: 0 }),
    });
    const data = await response.json();
    if (!response.ok || !isSuccessResponse(data.error)) {
      throw new Error(data.message || 'Failed to fetch outlets');
    }

    const normalizedOutlets = (Array.isArray(data.results) ? data.results : []).map(formatOutletRecord);
    setOutlets(normalizedOutlets);
    setSelectedOutlet(prev => prev || queryOutletId || normalizedOutlets[0]?.id || '');
  }, [queryOutletId]);

  const fetchCentralAccounts = useCallback(async token => {
    const response = await fetch(`${API_BASE_URL}accounting/accounts?limit=500`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await parseJsonResponse(response);
    if (!response.ok || !isSuccessResponse(data?.error)) {
      throw new Error(data?.message || 'Failed to fetch central accounts');
    }
    setCentralAccounts((Array.isArray(data?.results) ? data.results : []).map(formatCentralAccountRecord));
  }, []);

  const fetchFinanceData = useCallback(async (token, outletId) => {
    if (!outletId) {
      return;
    }

    setLoading(true);
    try {
      const [documentsResponse, pendingApprovalsResponse, paymentsResponse, summaryResponse] = await Promise.all([
        fetch(`${API_BASE_URL}fitness-finance/document/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            document_scope: 'operational',
            limit: 30,
            offset: 0,
          }),
        }),
        fetch(`${API_BASE_URL}fitness-finance/document/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            document_scope: 'operational',
            approval_status: 'pending',
            limit: 20,
            offset: 0,
          }),
        }),
        fetch(`${API_BASE_URL}fitness-finance/payment/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            document_scope: 'operational',
            limit: 30,
            offset: 0,
          }),
        }),
        fetch(`${API_BASE_URL}fitness-finance/report/owner-summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            year: currentPeriod.year,
            month: currentPeriod.month,
            recent_limit: 8,
          }),
        }),
      ]);

      const [documentsData, pendingApprovalsData, paymentsData, summaryData] = await Promise.all([
        parseJsonResponse(documentsResponse),
        parseJsonResponse(pendingApprovalsResponse),
        parseJsonResponse(paymentsResponse),
        parseJsonResponse(summaryResponse),
      ]);

      if (!documentsResponse.ok || !isSuccessResponse(documentsData?.error)) {
        throw new Error(buildFinanceApiErrorMessage(documentsResponse, documentsData, 'Failed to load finance documents'));
      }
      if (!pendingApprovalsResponse.ok || !isSuccessResponse(pendingApprovalsData?.error)) {
        throw new Error(buildFinanceApiErrorMessage(pendingApprovalsResponse, pendingApprovalsData, 'Failed to load finance approvals'));
      }
      if (!paymentsResponse.ok || !isSuccessResponse(paymentsData?.error)) {
        throw new Error(buildFinanceApiErrorMessage(paymentsResponse, paymentsData, 'Failed to load finance payments'));
      }
      if (!summaryResponse.ok || !isSuccessResponse(summaryData?.error)) {
        throw new Error(buildFinanceApiErrorMessage(summaryResponse, summaryData, 'Failed to load owner summary'));
      }

      setFinanceDocuments(Array.isArray(documentsData?.results) ? documentsData.results : []);
      setPendingApprovalDocuments(Array.isArray(pendingApprovalsData?.results) ? pendingApprovalsData.results : []);
      setFinancePayments(Array.isArray(paymentsData?.results) ? paymentsData.results : []);
      setOwnerSummary(summaryData?.data?.summary || null);
      setCanApproveBackdatedFinance(Boolean(documentsData?.meta?.can_approve_backdated ?? pendingApprovalsData?.meta?.can_approve_backdated));
      setFinanceApiError('');
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
      setFinanceDocuments([]);
      setPendingApprovalDocuments([]);
      setFinancePayments([]);
      setOwnerSummary(null);
      setCanApproveBackdatedFinance(false);
      setFinanceApiError(error.message || 'Failed to fetch finance data');
      Swal.fire('Error', error.message || 'Failed to fetch finance data', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPeriod.month, currentPeriod.year]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;
    const initialize = async () => {
      const token = await resolveToken();
      if (!token || !active) {
        return;
      }

      setAuthToken(token);
      try {
        await Promise.all([fetchOutlets(token), fetchCentralAccounts(token)]);
      } catch (error) {
        console.error('Failed to initialize fitness finance page:', error);
        Swal.fire('Error', error.message || 'Failed to initialize finance page', 'error');
      }
    };

    initialize();

    return () => {
      active = false;
    };
  }, [canAccess, fetchCentralAccounts, fetchOutlets, navigate, resolveToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchFinanceData(authToken, selectedOutlet);
  }, [authToken, fetchFinanceData, selectedOutlet]);

  const expenseAccounts = useMemo(
    () => centralAccounts.filter(account => String(account.account_type).toLowerCase() === 'expense'),
    [centralAccounts]
  );
  const assetAccounts = useMemo(
    () => centralAccounts.filter(account => String(account.account_type).toLowerCase() === 'asset'),
    [centralAccounts]
  );
  const liabilityAccounts = useMemo(
    () => centralAccounts.filter(account => String(account.account_type).toLowerCase() === 'liability'),
    [centralAccounts]
  );
  const outstandingPayableDocuments = useMemo(
    () =>
      financeDocuments.filter(
        item => Number(item?.outstanding_amount || 0) > 0 && isFinanceDocumentApprovedForLedger(item?.approval_status)
      ),
    [financeDocuments]
  );
  const selectedOutletData = useMemo(
    () => outlets.find(outlet => String(outlet.id) === String(selectedOutlet)) || null,
    [outlets, selectedOutlet]
  );
  const selectedPayableDocument = useMemo(
    () => outstandingPayableDocuments.find(item => String(item.id) === String(payablePaymentForm.document_id)) || null,
    [outstandingPayableDocuments, payablePaymentForm.document_id]
  );

  useEffect(() => {
    if (expenseAccounts.length > 0) {
      setFinanceForm(prev => (prev.expense_account_id ? prev : { ...prev, expense_account_id: expenseAccounts[0].account_id }));
    }
    if (assetAccounts.length > 0) {
      setFinanceForm(prev => (prev.payment_account_id ? prev : { ...prev, payment_account_id: assetAccounts[0].account_id }));
      setPayablePaymentForm(prev => (prev.payment_account_id ? prev : { ...prev, payment_account_id: assetAccounts[0].account_id }));
    }
    if (liabilityAccounts.length > 0) {
      setFinanceForm(prev => (prev.payable_account_id ? prev : { ...prev, payable_account_id: liabilityAccounts[0].account_id }));
    }
  }, [assetAccounts, expenseAccounts, liabilityAccounts]);

  useEffect(() => {
    if (!payablePaymentForm.document_id) {
      return;
    }

    const activeDocument = outstandingPayableDocuments.find(item => String(item.id) === String(payablePaymentForm.document_id));
    if (!activeDocument) {
      return;
    }

    setPayablePaymentForm(prev => (
      prev.amount
        ? prev
        : { ...prev, amount: String(Number(activeDocument.outstanding_amount || 0)) }
    ));
  }, [outstandingPayableDocuments, payablePaymentForm.document_id]);

  const resetFinanceForm = useCallback(() => {
    setFinanceForm({
      ...createEmptyFinanceForm(),
      expense_account_id: expenseAccounts[0]?.account_id || '',
      payment_account_id: assetAccounts[0]?.account_id || '',
      payable_account_id: liabilityAccounts[0]?.account_id || '',
    });
  }, [assetAccounts, expenseAccounts, liabilityAccounts]);

  const resetPayablePaymentForm = useCallback(() => {
    setPayablePaymentForm({
      ...createEmptyPayablePaymentForm(),
      payment_account_id: assetAccounts[0]?.account_id || '',
    });
  }, [assetAccounts]);

  const handleSaveFinanceDocument = useCallback(async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }
    if (!financeForm.description.trim()) {
      Swal.fire('Error', 'Deskripsi beban wajib diisi.', 'error');
      return;
    }
    if (Number(financeForm.amount || 0) <= 0) {
      Swal.fire('Error', 'Nominal finance wajib lebih dari 0.', 'error');
      return;
    }
    if (!isWholeRupiahValue(financeForm.amount)) {
      Swal.fire('Error', 'Nominal finance harus rupiah bulat tanpa sen.', 'error');
      return;
    }

    setSavingFinanceDocument(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/document/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_type: financeForm.settlement_mode === 'payable' ? 'payable_bill' : 'expense',
          settlement_mode: financeForm.settlement_mode,
          expense_date: financeForm.expense_date,
          due_date: financeForm.settlement_mode === 'payable' ? financeForm.due_date : '',
          vendor_name: financeForm.vendor_name,
          category: financeForm.category,
          description: financeForm.description,
          reference_no: financeForm.reference_no,
          amount: Number(financeForm.amount || 0),
          expense_account_id: financeForm.expense_account_id,
          payment_account_id: financeForm.settlement_mode === 'payable' ? '' : financeForm.payment_account_id,
          payable_account_id: financeForm.settlement_mode === 'payable' ? financeForm.payable_account_id : '',
          payment_method: financeForm.settlement_mode === 'payable' ? '' : financeForm.payment_method,
          notes: financeForm.notes,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildFinanceApiErrorMessage(response, data, 'Failed to save finance document'));
      }

      await fetchFinanceData(authToken, selectedOutlet);
      resetFinanceForm();
      Swal.fire('Success', data?.message || 'Dokumen finance berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save finance document:', error);
      Swal.fire('Error', error.message || 'Failed to save finance document', 'error');
    } finally {
      setSavingFinanceDocument(false);
    }
  }, [authToken, fetchFinanceData, financeForm, resetFinanceForm, selectedOutlet]);

  const handleSavePayablePayment = useCallback(async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }
    if (!payablePaymentForm.document_id) {
      Swal.fire('Error', 'Pilih dokumen hutang dulu.', 'error');
      return;
    }
    if (Number(payablePaymentForm.amount || 0) <= 0) {
      Swal.fire('Error', 'Nominal payment wajib lebih dari 0.', 'error');
      return;
    }

    const payableAmount = Number(payablePaymentForm.amount || 0);
    const outstandingAmount = Number(selectedPayableDocument?.outstanding_amount || 0);
    const allowsLegacyFraction =
      selectedPayableDocument &&
      !isWholeRupiahValue(outstandingAmount) &&
      Math.abs(payableAmount - outstandingAmount) < 0.0001;
    if (!allowsLegacyFraction && !isWholeRupiahValue(payablePaymentForm.amount)) {
      Swal.fire('Error', 'Nominal payment harus rupiah bulat tanpa sen.', 'error');
      return;
    }

    setSavingFinancePayment(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/payment/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_id: Number(payablePaymentForm.document_id),
          payment_date: payablePaymentForm.payment_date,
          amount: Number(payablePaymentForm.amount || 0),
          payment_method: payablePaymentForm.payment_method,
          payment_account_id: payablePaymentForm.payment_account_id,
          notes: payablePaymentForm.notes,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildFinanceApiErrorMessage(response, data, 'Failed to save payable payment'));
      }

      await fetchFinanceData(authToken, selectedOutlet);
      resetPayablePaymentForm();
      Swal.fire('Success', data?.message || 'Pembayaran hutang berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save payable payment:', error);
      Swal.fire('Error', error.message || 'Failed to save payable payment', 'error');
    } finally {
      setSavingFinancePayment(false);
    }
  }, [authToken, fetchFinanceData, payablePaymentForm, resetPayablePaymentForm, selectedOutlet, selectedPayableDocument]);

  const handleApproveFinanceDocument = useCallback(async row => {
    const confirmation = await Swal.fire({
      title: 'Approve dokumen backdate?',
      text: `${row?.document_no || 'Dokumen'} akan langsung masuk ke jurnal pusat.`,
      input: 'textarea',
      inputLabel: 'Catatan approval (opsional)',
      inputPlaceholder: 'Tambahkan catatan jika perlu',
      inputAttributes: {
        'aria-label': 'Catatan approval',
      },
      showCancelButton: true,
      confirmButtonText: 'Approve',
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setProcessingApproval(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/document/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_id: Number(row?.id || 0),
          approval_note: confirmation.value || '',
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildFinanceApiErrorMessage(response, data, 'Failed to approve finance document'));
      }

      await fetchFinanceData(authToken, selectedOutlet);
      Swal.fire('Success', data?.message || 'Dokumen finance berhasil diapprove.', 'success');
    } catch (error) {
      console.error('Failed to approve finance document:', error);
      Swal.fire('Error', error.message || 'Failed to approve finance document', 'error');
    } finally {
      setProcessingApproval(false);
    }
  }, [authToken, fetchFinanceData, selectedOutlet]);

  const handleRejectFinanceDocument = useCallback(async row => {
    const confirmation = await Swal.fire({
      title: 'Tolak dokumen backdate?',
      text: `${row?.document_no || 'Dokumen'} tidak akan masuk ke jurnal.`,
      input: 'textarea',
      inputLabel: 'Alasan penolakan',
      inputPlaceholder: 'Jelaskan alasan penolakan',
      inputAttributes: {
        'aria-label': 'Alasan penolakan',
      },
      showCancelButton: true,
      confirmButtonText: 'Tolak',
      preConfirm: value => {
        if (!String(value || '').trim()) {
          Swal.showValidationMessage('Alasan penolakan wajib diisi.');
          return false;
        }
        return value;
      },
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setProcessingApproval(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/document/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_id: Number(row?.id || 0),
          approval_note: confirmation.value || '',
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildFinanceApiErrorMessage(response, data, 'Failed to reject finance document'));
      }

      await fetchFinanceData(authToken, selectedOutlet);
      Swal.fire('Success', data?.message || 'Dokumen finance berhasil ditolak.', 'success');
    } catch (error) {
      console.error('Failed to reject finance document:', error);
      Swal.fire('Error', error.message || 'Failed to reject finance document', 'error');
    } finally {
      setProcessingApproval(false);
    }
  }, [authToken, fetchFinanceData, selectedOutlet]);

  const financeDocumentColumns = [
    {
      name: 'Dokumen',
      sortable: true,
      grow: 1.6,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.document_no}</strong>
          <small>{row.description}</small>
        </span>
      ),
    },
    {
      name: 'Kategori',
      width: '180px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{humanizeToken(row.category)}</strong>
          <small>{row.vendor_name || (row.settlement_mode === 'payable' ? 'Hutang usaha' : 'Beban langsung')}</small>
        </span>
      ),
    },
    {
      name: 'Tanggal',
      width: '180px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.expense_date)}</strong>
          <small>{row.due_date ? `Jatuh tempo ${formatShortDate(row.due_date)}` : 'Tidak ada due date'}</small>
        </span>
      ),
    },
    {
      name: 'Nominal',
      width: '210px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatCurrency(row.amount)}</strong>
          <small>Terbayar {formatCurrency(row.paid_amount)} | Sisa {formatCurrency(row.outstanding_amount)}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      width: '160px',
      cell: row => (
        <span className={`status-pill ${getFinanceStatusVariant(row.status)}`}>
          {FINANCE_STATUS_LABELS[row.status] || humanizeToken(row.status)}
        </span>
      ),
    },
    {
      name: 'Approval',
      width: '230px',
      cell: row => {
        const approvalStatus = normalizeApprovalStatus(row.approval_status);
        const journalMessage = isFinanceDocumentApprovedForLedger(approvalStatus)
          ? 'Sudah boleh memengaruhi jurnal'
          : 'Belum masuk jurnal pusat';
        const detailMessage =
          approvalStatus === 'pending'
            ? `Diajukan ${formatDateTime(row.approval_requested_at)}`
            : approvalStatus === 'approved'
              ? `Disetujui ${row.approval_decided_by_name || row.approval_decided_by_actor || 'manager/admin'}`
              : approvalStatus === 'rejected'
                ? row.approval_note || 'Dokumen ditolak'
                : 'Tidak butuh approval tambahan';

        return (
          <span className="table-identity__meta">
            <span className={`status-pill ${getFinanceApprovalVariant(approvalStatus)}`}>
              {FINANCE_APPROVAL_LABELS[approvalStatus] || humanizeToken(approvalStatus)}
            </span>
            <small>{detailMessage}</small>
            <small>{journalMessage}</small>
          </span>
        );
      },
    },
    {
      name: 'Aksi',
      width: '220px',
      cell: row => (
        normalizeApprovalStatus(row.approval_status) === 'pending' && canApproveBackdatedFinance ? (
          <div className="d-flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => handleApproveFinanceDocument(row)}
              disabled={processingApproval}
            >
              Approve
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline-danger"
              onClick={() => handleRejectFinanceDocument(row)}
              disabled={processingApproval}
            >
              Reject
            </Button>
          </div>
        ) : (
          <span className="table-identity__meta">
            <small>{row.journal_id ? `Jurnal #${row.journal_id}` : 'Tidak ada aksi'}</small>
          </span>
        )
      ),
    },
  ];

  const financePaymentColumns = [
    {
      name: 'Payment',
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.payment_no}</strong>
          <small>{row.document_no || row.document_description || 'Dokumen hutang'}</small>
        </span>
      ),
    },
    {
      name: 'Tanggal',
      width: '170px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.payment_date)}</strong>
          <small>{humanizeToken(row.payment_method)}</small>
        </span>
      ),
    },
    {
      name: 'Nominal',
      width: '180px',
      cell: row => <strong>{formatCurrency(row.amount)}</strong>,
    },
    {
      name: 'Dokumen',
      grow: 1.4,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.document_description || '-'}</strong>
          <small>{row.vendor_name || row.document_status || '-'}</small>
        </span>
      ),
    },
  ];

  const recentJournalColumns = [
    {
      name: 'Waktu',
      width: '180px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.occurred_at)}</strong>
          <small>{humanizeToken(row.source_type)}</small>
        </span>
      ),
    },
    {
      name: 'Deskripsi',
      grow: 1.8,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.description}</strong>
          <small>{row.debit_account} -> {row.credit_account}</small>
        </span>
      ),
    },
    {
      name: 'Nilai',
      width: '170px',
      cell: row => <strong>{formatCurrency(row.amount)}</strong>,
    },
  ];

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaReceipt}
          eyebrow="Expenses & Payables"
          title="Expenses & Payables"
          description="Workspace khusus untuk catat beban usaha, hutang vendor, pembayaran hutang, dan membaca laporan owner outlet."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Expenses & Payables' },
          ]}
          meta={(
            <div className="d-flex flex-wrap gap-2">
              <span className="soft-stat">
                <FaBalanceScale /> {selectedOutletData?.name || 'Select outlet'}
              </span>
              <span className="soft-stat">
                <FaChartLine /> {currentPeriod.label}
              </span>
            </div>
          )}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="primary"
                onClick={() => navigate(buildAccountingReportCenterPath('profit-loss', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaChartLine /> Analytics Center
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/asset-management?outlet_id=${selectedOutlet}`)}
                disabled={!selectedOutlet}
              >
                <FaBalanceScale /> Assets & Investments
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/accounting-setup?outlet_id=${selectedOutlet}`)}
                disabled={!selectedOutlet}
              >
                <FaWallet /> Accounting Control
              </Button>
            </div>
          )}
        />

        <div className="section-card accounting-filter-card">
          <div className="table-toolbar">
            <div className="control-with-icon search-control accounting-outlet-picker">
              <span className="control-with-icon__icon">
                <FaSearch />
              </span>
              <Form.Select value={selectedOutlet} onChange={event => setSelectedOutlet(event.target.value)}>
                <option value="">Select outlet</option>
                {outlets.map(outlet => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="accounting-filter-card__note">
              Page ini memakai chart of accounts pusat. Pastikan akun beban, hutang, dan kas/bank sudah tersedia di `Accounting Control`.
            </div>
          </div>
        </div>

        {financeApiError ? (
          <div className="section-card accounting-alert-card">
            <div className="accounting-alert-card__copy">
              <span className="page-eyebrow">Connection issue</span>
              <h2>Expenses & Payables belum bisa memuat data</h2>
              <p>{financeApiError}</p>
            </div>
            <div className="accounting-alert-card__actions">
              <Button variant="primary" onClick={() => authToken && selectedOutlet && fetchFinanceData(authToken, selectedOutlet)}>
                <FaCheckCircle /> Coba lagi
              </Button>
            </div>
          </div>
        ) : null}

        <div className="accounting-kpi-grid">
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Pendapatan</span>
            <h3>{formatCurrency(ownerSummary?.profit_loss?.revenue)}</h3>
            <p>Pendapatan yang sudah masuk ke ledger pusat pada periode berjalan.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Beban usaha</span>
            <h3>{formatCurrency(ownerSummary?.profit_loss?.expenses)}</h3>
            <p>Total beban operasional outlet pada periode ini.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Net profit</span>
            <h3>{formatCurrency(ownerSummary?.profit_loss?.net_profit)}</h3>
            <p>Laba bersih owner view untuk outlet yang dipilih.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Outstanding hutang</span>
            <h3>{formatCurrency(ownerSummary?.payable_summary?.outstanding_total)}</h3>
            <p>{ownerSummary?.payable_summary?.open_bills || 0} dokumen hutang masih terbuka.</p>
          </section>
        </div>

        <section className="section-card accounting-insight-card">
          <div className="accounting-section-head">
            <div>
              <span className="page-eyebrow">Panduan cepat</span>
              <h2>Apa yang ingin Anda lakukan hari ini?</h2>
              <p>Untuk user pemula, pilih satu tugas. Halaman akan fokus ke proses itu saja.</p>
            </div>
            <span className="status-pill">
              <FaBalanceScale /> Guided mode
            </span>
          </div>

          <div className="accounting-report-flow">
            {WORKSPACE_OPTIONS.map(item => (
              <button
                key={item.key}
                type="button"
                className={`accounting-report-flow__item accounting-report-flow__item--button ${activeWorkspace === item.key ? 'is-active' : ''}`}
                onClick={() => setActiveWorkspace(item.key)}
              >
                <strong>{item.title}</strong>
                <small>{item.copy}</small>
              </button>
            ))}
          </div>
        </section>

        {activeWorkspace === 'expense' ? (
        <div className="accounting-panel-grid">
          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Input operasional</span>
                <h2>Beban usaha dan beban lain-lain</h2>
                <p>Pilih bayar langsung atau catat sebagai hutang. Jika tanggal dibuat untuk hari yang sudah lewat, staf akan masuk antrean approval manajer/admin sebelum posting ke ledger pusat.</p>
              </div>
              <span className="status-pill">
                <FaReceipt /> Finance doc
              </span>
            </div>

            <Form onSubmit={handleSaveFinanceDocument}>
              <div className="form-grid">
                <Form.Group className="field-stack span-4">
                  <Form.Label>Metode pencatatan</Form.Label>
                  <Form.Select
                    value={financeForm.settlement_mode}
                    onChange={event => setFinanceForm(prev => ({ ...prev, settlement_mode: event.target.value }))}
                  >
                    {FINANCE_SETTLEMENT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Tanggal dokumen</Form.Label>
                  <Form.Control
                    type="date"
                    value={financeForm.expense_date}
                    onChange={event => setFinanceForm(prev => ({ ...prev, expense_date: event.target.value }))}
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Kategori</Form.Label>
                  <Form.Select
                    value={financeForm.category}
                    onChange={event => setFinanceForm(prev => ({ ...prev, category: event.target.value }))}
                  >
                    {FINANCE_CATEGORY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-6">
                  <Form.Label>Vendor / payee</Form.Label>
                  <Form.Control
                    value={financeForm.vendor_name}
                    onChange={event => setFinanceForm(prev => ({ ...prev, vendor_name: event.target.value }))}
                    placeholder="Contoh: PLN, owner building, vendor ATK"
                  />
                </Form.Group>
                <Form.Group className="field-stack span-6">
                  <Form.Label>Nomor referensi</Form.Label>
                  <Form.Control
                    value={financeForm.reference_no}
                    onChange={event => setFinanceForm(prev => ({ ...prev, reference_no: event.target.value }))}
                    placeholder="No invoice / kuitansi"
                  />
                </Form.Group>
                <Form.Group className="field-stack span-12">
                  <Form.Label>Deskripsi</Form.Label>
                  <Form.Control
                    value={financeForm.description}
                    onChange={event => setFinanceForm(prev => ({ ...prev, description: event.target.value }))}
                    placeholder="Contoh: Sewa outlet Maret 2026"
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Nominal</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    value={financeForm.amount}
                    onChange={event => setFinanceForm(prev => ({ ...prev, amount: event.target.value }))}
                    placeholder="0"
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Akun beban</Form.Label>
                  <Form.Select
                    value={financeForm.expense_account_id}
                    onChange={event => setFinanceForm(prev => ({ ...prev, expense_account_id: event.target.value }))}
                    required
                  >
                    <option value="">Pilih akun beban</option>
                    {expenseAccounts.map(account => (
                      <option key={account.account_id} value={account.account_id}>
                        {account.account_id} - {account.account_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {financeForm.settlement_mode === 'payable' ? (
                  <>
                    <Form.Group className="field-stack span-4">
                      <Form.Label>Jatuh tempo</Form.Label>
                      <Form.Control
                        type="date"
                        value={financeForm.due_date}
                        onChange={event => setFinanceForm(prev => ({ ...prev, due_date: event.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="field-stack span-12">
                      <Form.Label>Akun hutang</Form.Label>
                      <Form.Select
                        value={financeForm.payable_account_id}
                        onChange={event => setFinanceForm(prev => ({ ...prev, payable_account_id: event.target.value }))}
                        required
                      >
                        <option value="">Pilih akun hutang</option>
                        {liabilityAccounts.map(account => (
                          <option key={account.account_id} value={account.account_id}>
                            {account.account_id} - {account.account_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </>
                ) : (
                  <>
                    <Form.Group className="field-stack span-4">
                      <Form.Label>Metode bayar</Form.Label>
                      <Form.Select
                        value={financeForm.payment_method}
                        onChange={event => setFinanceForm(prev => ({ ...prev, payment_method: event.target.value }))}
                      >
                        {FINANCE_SETTLEMENT_OPTIONS.filter(option => option.value !== 'payable').map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="field-stack span-8">
                      <Form.Label>Akun pembayaran</Form.Label>
                      <Form.Select
                        value={financeForm.payment_account_id}
                        onChange={event => setFinanceForm(prev => ({ ...prev, payment_account_id: event.target.value }))}
                        required
                      >
                        <option value="">Pilih akun asset</option>
                        {assetAccounts.map(account => (
                          <option key={account.account_id} value={account.account_id}>
                            {account.account_id} - {account.account_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </>
                )}
                <Form.Group className="field-stack span-12">
                  <Form.Label>Catatan</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={financeForm.notes}
                    onChange={event => setFinanceForm(prev => ({ ...prev, notes: event.target.value }))}
                    placeholder="Catatan tambahan untuk owner atau audit."
                  />
                </Form.Group>
                <div className="field-stack span-12 accounting-action-stack">
                  <div className="d-flex flex-wrap gap-2">
                    <Button type="submit" disabled={!selectedOutlet || savingFinanceDocument || expenseAccounts.length === 0}>
                      <FaPlus /> {savingFinanceDocument ? 'Menyimpan...' : 'Simpan dokumen finance'}
                    </Button>
                    <Button type="button" variant="outline-secondary" onClick={resetFinanceForm}>
                      Reset form
                    </Button>
                  </div>
                </div>
              </div>
            </Form>

            <div className="accounting-inline-note">
              Flow owner: `buat dokumen` -> jika hutang maka outstanding bertambah -> saat dibayar sistem membuat jurnal
              `debit hutang / credit kas-bank`. Dokumen backdate yang masih `pending approval` belum memengaruhi jurnal dan belum bisa dibayar.
            </div>
          </section>
        </div>
        ) : null}

        {activeWorkspace === 'payable' ? (
        <div className="accounting-panel-grid">
          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Pembayaran hutang</span>
                <h2>Lunasi vendor bill</h2>
                <p>Pilih dokumen yang masih outstanding. Saldo hutang dan history payment akan diperbarui otomatis.</p>
              </div>
              <span className={`status-pill ${outstandingPayableDocuments.length ? 'is-warning' : ''}`}>
                <FaFileInvoiceDollar /> {outstandingPayableDocuments.length} open bill
              </span>
            </div>

            <Form onSubmit={handleSavePayablePayment}>
              <div className="form-grid">
                <Form.Group className="field-stack span-12">
                  <Form.Label>Dokumen hutang</Form.Label>
                  <Form.Select
                    value={payablePaymentForm.document_id}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, document_id: event.target.value, amount: '' }))}
                    required
                  >
                    <option value="">Pilih hutang yang akan dibayar</option>
                    {outstandingPayableDocuments.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.document_no} - {item.description} - sisa {formatCurrency(item.outstanding_amount)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Tanggal payment</Form.Label>
                  <Form.Control
                    type="date"
                    value={payablePaymentForm.payment_date}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_date: event.target.value }))}
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Nominal payment</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={payablePaymentForm.amount}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, amount: event.target.value }))}
                    placeholder="0"
                    required
                  />
                </Form.Group>
                <Form.Group className="field-stack span-4">
                  <Form.Label>Metode bayar</Form.Label>
                  <Form.Select
                    value={payablePaymentForm.payment_method}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_method: event.target.value }))}
                  >
                    {FINANCE_SETTLEMENT_OPTIONS.filter(option => option.value !== 'payable').map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-12">
                  <Form.Label>Akun pembayaran</Form.Label>
                  <Form.Select
                    value={payablePaymentForm.payment_account_id}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_account_id: event.target.value }))}
                    required
                  >
                    <option value="">Pilih akun asset</option>
                    {assetAccounts.map(account => (
                      <option key={account.account_id} value={account.account_id}>
                        {account.account_id} - {account.account_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="field-stack span-12">
                  <Form.Label>Catatan payment</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={payablePaymentForm.notes}
                    onChange={event => setPayablePaymentForm(prev => ({ ...prev, notes: event.target.value }))}
                    placeholder="Catatan pembayaran hutang"
                  />
                </Form.Group>
                <div className="field-stack span-12 accounting-action-stack">
                  <div className="d-flex flex-wrap gap-2">
                    <Button type="submit" disabled={!selectedOutlet || savingFinancePayment || outstandingPayableDocuments.length === 0}>
                      <FaMoneyCheckAlt /> {savingFinancePayment ? 'Menyimpan...' : 'Simpan pembayaran hutang'}
                    </Button>
                    <Button type="button" variant="outline-secondary" onClick={resetPayablePaymentForm}>
                      Reset form
                    </Button>
                  </div>
                </div>
              </div>
            </Form>

            <div className="accounting-inline-note">
              {selectedPayableDocument ? (
                <>
                  Dokumen terpilih: <strong>{selectedPayableDocument.document_no}</strong> dengan outstanding{' '}
                  <strong>{formatCurrency(selectedPayableDocument.outstanding_amount)}</strong>.
                </>
              ) : (
                'Pilih dokumen hutang yang masih outstanding untuk men-generate jurnal pembayaran.'
              )}
            </div>
          </section>
        </div>
        ) : null}

        {activeWorkspace === 'overview' ? (
        <div className="accounting-panel-grid">
          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Owner snapshot</span>
                <h2>Laporan akuntansi bulan ini</h2>
                <p>Ringkasan ini diambil dari ledger pusat, jadi pendapatan dan beban operasional ada di view yang sama.</p>
              </div>
              <span className="status-pill">
                <FaChartLine /> {currentPeriod.label}
              </span>
            </div>

            <div className="accounting-mini-stats">
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.profit_loss?.revenue)}</strong>
                <small>Pendapatan</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.profit_loss?.expenses)}</strong>
                <small>Beban usaha</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.balance_sheet?.assets)}</strong>
                <small>Total assets</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.balance_sheet?.liabilities)}</strong>
                <small>Total liabilities</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.balance_sheet?.equity)}</strong>
                <small>Equity</small>
              </div>
              <div className="accounting-mini-stat">
                <strong>{formatCurrency(ownerSummary?.payable_summary?.overdue_total)}</strong>
                <small>Hutang lewat jatuh tempo</small>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-3">
              <Button
                variant="primary"
                onClick={() => navigate(buildAccountingReportCenterPath('profit-loss', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaChartLine /> Profit & Loss
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(buildAccountingReportCenterPath('balance-sheet', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaBalanceScale /> Balance Sheet
              </Button>
            </div>
          </section>

          <section className="section-card accounting-insight-card">
            <div className="accounting-section-head">
              <div>
                <span className="page-eyebrow">Report flow</span>
                <h2>Alur review owner</h2>
                <p>Bacaan owner dibuat bertahap dari kontrol harian sampai audit trail.</p>
              </div>
              <span className="status-pill">
                <FaBalanceScale /> Owner flow
              </span>
            </div>

            <div className="accounting-report-flow">
              {OWNER_REPORT_FLOW.map(item => (
                <div key={item.title} className="accounting-report-flow__item">
                  <strong>{item.title}</strong>
                  <small>{item.copy}</small>
                </div>
              ))}
            </div>

            <div className="accounting-breakdown-list">
              {(ownerSummary?.expense_breakdown || []).slice(0, 6).map(item => (
                <div key={item.category} className="accounting-breakdown-item">
                  <span>{humanizeToken(item.category)}</span>
                  <strong>{formatCurrency(item.total)}</strong>
                </div>
              ))}
              {!(ownerSummary?.expense_breakdown || []).length ? (
                <div className="accounting-inline-note mt-0">Belum ada breakdown beban pada periode ini.</div>
              ) : null}
            </div>
          </section>
        </div>
        ) : null}

        {activeWorkspace === 'report' ? (
        <>
        {canApproveBackdatedFinance || pendingApprovalDocuments.length ? (
        <section className="section-card table-card">
          <div className="table-toolbar">
            <span className={`status-chip ${pendingApprovalDocuments.length ? '' : 'is-muted'}`}>
              <FaCheckCircle /> {pendingApprovalDocuments.length} backdate menunggu approval
            </span>
            <span className="accounting-filter-card__note">
              Hanya setelah dokumen diapprove, jurnal pusat akan dibuat dan angka owner ikut berubah.
            </span>
          </div>
          <AdminDataTable
            columns={financeDocumentColumns}
            data={pendingApprovalDocuments}
            progressPending={loading || processingApproval}
            pagination
            highlightOnHover
            striped
            dense
            emptyTitle="Belum ada backdate yang menunggu approval"
            emptyDescription="Semua dokumen bertanggal lampau untuk outlet ini sudah diproses."
          />
        </section>
        ) : null}

        <section className="section-card table-card">
          <div className="table-toolbar">
            <span className="status-chip">
              <FaReceipt /> {financeDocuments.length} dokumen finance terbaru
            </span>
            <span className="status-chip">
              <FaFileInvoiceDollar /> {ownerSummary?.payable_summary?.open_bills || 0} hutang masih terbuka
            </span>
          </div>
          <AdminDataTable
            columns={financeDocumentColumns}
            data={financeDocuments}
            progressPending={loading || processingApproval}
            pagination
            highlightOnHover
            striped
            dense
            emptyTitle="Belum ada dokumen finance"
            emptyDescription="Beban usaha, hutang, dan top up pembayaran vendor akan muncul di sini."
          />
        </section>

        <div className="accounting-panel-grid">
          <section className="section-card table-card">
            <div className="table-toolbar">
              <span className="status-chip">
                <FaMoneyCheckAlt /> {financePayments.length} payment hutang terbaru
              </span>
            </div>
            <AdminDataTable
              columns={financePaymentColumns}
              data={financePayments}
              progressPending={loading}
              pagination
              highlightOnHover
              striped
              dense
              emptyTitle="Belum ada pembayaran hutang"
              emptyDescription="Payment terhadap hutang vendor akan tercatat di sini."
            />
          </section>

          <section className="section-card table-card">
            <div className="table-toolbar">
              <span className="status-chip">
                <FaCheckCircle /> {(ownerSummary?.recent_journals || []).length} jurnal terbaru
              </span>
            </div>
            <AdminDataTable
              columns={recentJournalColumns}
              data={ownerSummary?.recent_journals || []}
              progressPending={loading}
              pagination
              highlightOnHover
              striped
              dense
              emptyTitle="Belum ada jurnal operasional"
              emptyDescription="Aktivitas jurnal operasional terbaru akan tampil di sini."
            />
          </section>
        </div>
        </>
        ) : null}
      </Container>
    </div>
  );
}

export default FitnessFinanceManager;
